import React, { useState, useCallback, ReactElement } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
} from "@mui/material";
import { manualRevertTrade } from "src/api";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";

const useRevertDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [rowOptions, setRowOptions] = useState<{
    userId: string;
    tradeId: string;
  }>({
    userId: "",
    tradeId: "",
  });
  const { user: adminUser, getAccessTokenSilently } = useAuth0();
  const dialogOptions = {
    title: "Revert trade",
    description:
      "Carrying this out will revert the users trade and return the bet amount to their balance.",
    onConfirm: async (reason: string) => {
      const token = await getAccessTokenSilently();

      if (!token) return;

      if (!rowOptions.tradeId || !rowOptions.userId) {
        console.error("Trade ID or User ID is missing");
        return;
      }

      try {
        await manualRevertTrade({
          tradeId: rowOptions.tradeId,
          adminUserId: adminUser?.sub || "admin-id-unavailable",
          adminReason: reason,
          userId: rowOptions.userId,
          token: token,
        });
        toast.success("Trade successfully reverted.");
      } catch (error) {
        console.error("Error reverting trade:", error);
        toast.error(
          "Oops! An error has occurred reverting the trade! Please try again."
        );
      }
    },
    confirmButtonText: "Revert Trade",
    cancelButtonText: "Cancel",
  };

  const openDialog = useCallback(
    ({ userId, tradeId }: { userId: string; tradeId: string }) => {
      setRowOptions({
        userId,
        tradeId,
      });
      setIsOpen(true);
    },
    []
  );

  const closeDialog = useCallback(() => {
    setIsOpen(false);
  }, []);

  const ErrorMessage = ({ reason }: { reason: string }) => {
    if (reason.trim() === "") {
      return (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: "block" }}
        >
          Reason is required.
        </Typography>
      );
    }
    return null;
  };

  const DialogComponent: React.FC = () => {
    const {
      title,
      description,
      onConfirm,
      confirmButtonText,
      cancelButtonText,
    } = dialogOptions;
    const [reason, setReason] = useState("");

    return (
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography>{description}</Typography>
          <TextField
            fullWidth
            size="small"
            label="Reason"
            name={`admin-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={reason.trim() === ""}
            helperText={<ErrorMessage reason={reason} />}
            style={{ margin: "10px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="primary">
            {cancelButtonText}
          </Button>
          <Button
            onClick={() => {
              onConfirm(reason);
              closeDialog();
            }}
            color="primary"
            autoFocus
            disabled={reason.trim() === ""}
          >
            {confirmButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return { openDialog, closeDialog, DialogComponent };
};

export default useRevertDialog;
